import { Accessor, createSignal } from "solid-js";

import {
  FONT_KEYS,
  Fonts,
  MONOSPACE_FONT_KEYS,
  MonospaceFonts,
} from "@revolt/ui/themes/fonts";

import { State } from "..";

import { AbstractStore } from ".";

/**
 * Konkord: default accent (the symbol's violet) and the blue it replaced
 */
export const KONKORD_ACCENT = "#8b5cf6";
const LEGACY_ACCENT = "#5470ec";

export type TypeTheme = {
  /**
   * Base theme preset
   */
  preset: "you";

  /**
   * Light/dark mode
   */
  mode: "light" | "dark" | "system";

  /**
   * Accent
   * (Material You)
   */
  m3Accent: string;

  /**
   * Constrast
   * (Material You)
   */
  m3Contrast: number;

  /**
   * Variant
   * (Material You)
   */
  m3Variant:
    | "monochrome"
    | "neutral"
    | "tonal_spot"
    | "vibrant"
    | "expressive"
    | "fidelity"
    | "content"
    | "rainbow"
    | "fruit_salad";

  /**
   * Konkord: background surfaces in dark mode
   * (default = as generated, grey = lighter low-chroma greys, black = OLED)
   */
  m3Surface: "default" | "grey" | "black" | "space";

  /**
   * Konkord: colour the backgrounds take their hue from (null = the accent's)
   */
  surfaceTint: string | null;

  /**
   * Konkord: text colour (null = generated from the theme)
   */
  textColour: string | null;

  /**
   * Konkord: gradient behind the whole app ("none" = flat background)
   */
  backdrop: "none" | "linear" | "glow" | "aurora";

  /**
   * Konkord: gradient colours, direction (degrees) and strength (0-100)
   */
  backdropFrom: string;
  backdropTo: string;
  backdropAngle: number;
  backdropStrength: number;

  /**
   * Konkord: stars over the background
   */
  stars: boolean;

  /**
   * Konkord: corner roundness, 0.5 (squarer) to 1.5 (rounder)
   */
  roundness: number;

  /**
   * Whether to permit blurry surfaces
   */
  blur: boolean;

  /**
   * Interface font
   */
  interfaceFont: Fonts;

  /**
   * Monospace font
   */
  monospaceFont: MonospaceFonts;

  /**
   * Message size
   */
  messageSize: number;

  /**
   * Spacing between message groups
   */
  messageGroupSpacing: number;
};

export type SelectedTheme = Pick<
  TypeTheme,
  | "surfaceTint"
  | "textColour"
  | "roundness"
  | "blur"
  | "interfaceFont"
  | "monospaceFont"
  | "messageSize"
  | "messageGroupSpacing"
> & {
  preset: "you";
  darkMode: boolean;

  accent: string;
  contrast: number;
  variant: TypeTheme["m3Variant"];
  surface: TypeTheme["m3Surface"];
};

/**
 * Konkord: "#rrggbb" (what the colour generator and CSS mixes expect)
 */
function isHex(value: unknown): value is string {
  return typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Manages theme information
 */
export class Theme extends AbstractStore<"theme", TypeTheme> {
  prefersDark: Accessor<boolean>;

  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "theme");

    // handle prefers-color-scheme value and changes
    const [prefersDark, setPrefersDark] = createSignal(
      window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches,
    );

    this.prefersDark = prefersDark;

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (event) => setPrefersDark(event.matches));

    this.toggleBlur = this.toggleBlur.bind(this);
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeTheme {
    return {
      preset: "you",
      mode: "system",

      // Konkord: the symbol's violet
      m3Accent: KONKORD_ACCENT,
      m3Contrast: 0.0,
      m3Variant: "tonal_spot",
      m3Surface: "default",

      surfaceTint: null,
      textColour: null,
      backdrop: "none",
      backdropFrom: "#7c3aed",
      backdropTo: "#0ea5e9",
      backdropAngle: 135,
      backdropStrength: 35,
      stars: false,
      roundness: 1,

      interfaceFont: "Inter",
      monospaceFont: "Fira Code",

      blur: true,
      messageSize: 14,
      messageGroupSpacing: 12,
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeTheme>): TypeTheme {
    const data: TypeTheme = this.default();

    if (["light", "dark", "system"].includes(input.mode!)) {
      data.mode = input.mode!;
    }

    if (["you", "neutral"].includes(input.preset!)) {
      data.preset = input.preset!;
    }

    // Konkord: clamp to the range the colour generator accepts
    if (
      typeof input.m3Contrast === "number" &&
      Number.isFinite(input.m3Contrast)
    ) {
      data.m3Contrast = Math.min(1, Math.max(-1, input.m3Contrast));
    }

    // Konkord: anchored, so junk around a colour doesn't reach the generator
    if (
      typeof input.m3Accent === "string" &&
      input.m3Accent.match(
        /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
      )
    ) {
      // Konkord: the old default blue becomes the new default violet
      data.m3Accent =
        input.m3Accent.toLowerCase() === LEGACY_ACCENT
          ? KONKORD_ACCENT
          : input.m3Accent;
    }

    if (
      [
        "monochrome",
        "neutral",
        "tonal_spot",
        "vibrant",
        "expressive",
        "fidelity",
        "content",
        "rainbow",
        "fruit_salad",
      ].includes(input.m3Variant!)
    ) {
      data.m3Variant = input.m3Variant!;
    }

    if (["default", "grey", "black", "space"].includes(input.m3Surface!)) {
      data.m3Surface = input.m3Surface!;
    }

    // Konkord: optional colours, gradient and shape
    if (isHex(input.surfaceTint)) data.surfaceTint = input.surfaceTint;
    if (isHex(input.textColour)) data.textColour = input.textColour;

    if (["none", "linear", "glow", "aurora"].includes(input.backdrop!)) {
      data.backdrop = input.backdrop!;
    }
    if (isHex(input.backdropFrom)) data.backdropFrom = input.backdropFrom;
    if (isHex(input.backdropTo)) data.backdropTo = input.backdropTo;
    if (isNumber(input.backdropAngle)) {
      data.backdropAngle =
        ((Math.round(input.backdropAngle) % 360) + 360) % 360;
    }
    if (isNumber(input.backdropStrength)) {
      data.backdropStrength = Math.min(
        100,
        Math.max(5, Math.round(input.backdropStrength)),
      );
    }
    // stars came with the night blue background before they had their own
    // option
    data.stars =
      typeof input.stars === "boolean"
        ? input.stars
        : data.m3Surface === "space";
    if (isNumber(input.roundness)) {
      data.roundness = Math.min(1.5, Math.max(0.5, input.roundness));
    }

    if (typeof input.blur === "boolean") {
      data.blur = input.blur;
    }

    // Konkord: clamped to the sliders' ranges
    if (isNumber(input.messageSize)) {
      data.messageSize = Math.min(24, Math.max(12, input.messageSize));
    }

    if (isNumber(input.messageGroupSpacing)) {
      data.messageGroupSpacing = Math.min(
        16,
        Math.max(0, input.messageGroupSpacing),
      );
    }

    if (
      typeof input.monospaceFont === "string" &&
      MONOSPACE_FONT_KEYS.includes(input.monospaceFont)
    ) {
      data.monospaceFont = input.monospaceFont;
    }

    if (
      typeof input.interfaceFont === "string" &&
      FONT_KEYS.includes(input.interfaceFont)
    ) {
      data.interfaceFont = input.interfaceFont;
    }

    return data;
  }

  /**
   * Get the currently selected theme (considering system settings)
   */
  get activeTheme(): SelectedTheme {
    const opts = this.get();

    switch (opts.preset) {
      case "you":
        return {
          surfaceTint: opts.surfaceTint,
          textColour: opts.textColour,
          roundness: opts.roundness,
          blur: opts.blur,
          interfaceFont: opts.interfaceFont,
          monospaceFont: opts.monospaceFont,
          messageSize: opts.messageSize,
          messageGroupSpacing: opts.messageGroupSpacing,
          preset: "you",
          darkMode:
            opts.mode === "dark" ||
            (opts.mode === "system" && this.prefersDark()),

          accent: opts.m3Accent,
          contrast: opts.m3Contrast,
          variant: opts.m3Variant,
          surface: opts.m3Surface,
        };
    }
  }

  /**
   * Konkord: every option (read only)
   */
  get options(): Readonly<TypeTheme> {
    return this.get();
  }

  /**
   * Get light/dark/system mode
   */
  get mode() {
    return this.get().mode;
  }

  /**
   * Set light/dark/system mode
   * @param mode Mode
   */
  setMode(mode: TypeTheme["mode"]) {
    this.set("mode", mode);
  }

  /**
   * Get current preset
   */
  get preset() {
    return this.get().preset;
  }

  /**
   * Set the active preset
   * @param preset Preset
   */
  setPreset(preset: TypeTheme["preset"]) {
    this.set("preset", preset);
  }

  /**
   * Get current accent
   */
  get m3Accent() {
    return this.get().m3Accent;
  }

  /**
   * Set the accent of the Material You theme
   * @param accent Accent
   */
  setM3Accent(accent: string) {
    this.set("m3Accent", accent);
  }

  /**
   * Get current contrast
   */
  get m3Contrast() {
    return this.get().m3Contrast;
  }

  /**
   * Set the contrast of the Material You theme
   * @param contrast Contrast
   */
  setM3Contrast(contrast: number) {
    this.set("m3Contrast", contrast);
  }

  /**
   * Get current variant
   */
  get m3Variant() {
    return this.get().m3Variant;
  }

  /**
   * Set the variant of the Material You theme
   * @param variant Variant
   */
  setM3Variant(variant: TypeTheme["m3Variant"]) {
    this.set("m3Variant", variant);
  }

  /**
   * Konkord: get current dark mode surfaces
   */
  get m3Surface() {
    return this.get().m3Surface;
  }

  /**
   * Konkord: set dark mode surfaces
   * @param surface Surfaces
   */
  setM3Surface(surface: TypeTheme["m3Surface"]) {
    this.set("m3Surface", surface);
  }

  /**
   * Konkord: apply several options at once (theme presets, custom colours)
   * @param values Options
   */
  applyColours(values: Partial<TypeTheme>) {
    this.set(this.clean({ ...this.get(), ...values }));
  }

  /**
   * Konkord: back to the default colours and background (fonts and sizes
   * kept)
   */
  resetColours() {
    const defaults = this.default();
    this.applyColours({
      mode: defaults.mode,
      m3Accent: defaults.m3Accent,
      m3Contrast: defaults.m3Contrast,
      m3Variant: defaults.m3Variant,
      m3Surface: defaults.m3Surface,
      surfaceTint: null,
      textColour: null,
      backdrop: defaults.backdrop,
      backdropFrom: defaults.backdropFrom,
      backdropTo: defaults.backdropTo,
      backdropAngle: defaults.backdropAngle,
      backdropStrength: defaults.backdropStrength,
      stars: false,
    });
  }

  /**
   * Konkord: back to the default fonts, sizes and shape (colours kept)
   */
  resetText() {
    const defaults = this.default();
    this.applyColours({
      interfaceFont: defaults.interfaceFont,
      monospaceFont: defaults.monospaceFont,
      messageSize: defaults.messageSize,
      messageGroupSpacing: defaults.messageGroupSpacing,
      roundness: defaults.roundness,
      blur: defaults.blur,
    });
  }

  /**
   * Get current blur state
   */
  get blur() {
    return this.get().blur;
  }

  /**
   * Toggle blur state
   */
  toggleBlur() {
    this.set("blur", !this.blur);
  }

  /**
   * Get current interface font
   */
  get interfaceFont() {
    return this.get().interfaceFont;
  }

  /**
   * Set interface font
   */
  setInterfaceFont(font: Fonts) {
    return this.set("interfaceFont", font);
  }

  /**
   * Get current monospace font
   */
  get monospaceFont() {
    return this.get().monospaceFont;
  }

  /**
   * Set monospace font
   */
  setMonospaceFont(font: MonospaceFonts) {
    return this.set("monospaceFont", font);
  }

  /**
   * Get current message size
   */
  get messageSize() {
    return this.get().messageSize;
  }

  /**
   * Set message size
   */
  set messageSize(size: number) {
    this.set("messageSize", size);
  }

  /**
   * Get current message group spacing
   */
  get messageGroupSpacing() {
    return this.get().messageGroupSpacing;
  }

  /**
   * Set message group spacing
   */
  set messageGroupSpacing(space: number) {
    this.set("messageGroupSpacing", space);
  }
}
